export type RateSettingsField =
  | 'base_charge_rate_cents'
  | 'charge_rate_cents'
  | 'charge_rate_8hr'
  | 'charge_rate_12hr'
  | 'base_margin_percent'
  | 'base_margin_percent_w2_tcv'
  | 'base_margin_percent_w2_travel'
  | 'margin_percent_1099'
  | 'incremental_margin_percent_1099'
  | 'base_margin_percent_w2'
  | 'total_margin_percent_w2'
  | 'incremental_margin_percent_w2'
  | 'use_blended_rate'
  | 'payout_max_weekly_regular_hours'
  | 'payout_seventh_day_rule_enabled'
  | 'charge_max_weekly_regular_hours'
  | 'charge_seventh_day_rule_enabled'
  | 'blended_non_blended_settings'
  | 'margin_percent_w2_travel'
  | 'margin_percent_w2_tcv'
  | 'margin_w2_tcv_cents'
  | 'assignment_bonus_tier2_travel_amount_cents'
  | 'assignment_bonus_tier2_travel_amount_tcv_cents'
  | 'assignment_bonus_tier1_travel_amount_cents'
  | 'assignment_bonus_tier1_travel_amount_tcv_cents'
  | 'invoicing_markup'
  | 'misc_markup'
  | 'operational_markup'
  | 'pro_rate';

export interface FieldVisibilityConfig {
  [resource: string]: {
    hiddenFields: RateSettingsField[];
  };
}

export const sources: {
  ten99: RateSettingsField[];
  'w2.local': RateSettingsField[];
  'w2.travel': RateSettingsField[];
} = {
  ten99: [
    'base_charge_rate_cents',
    'charge_rate_cents',
    'base_margin_percent',
    'margin_percent_1099',
    'invoicing_markup',
    'misc_markup',
  ],
  ['w2.local']: [
    'base_charge_rate_cents',
    'charge_rate_cents',
    'base_margin_percent_w2',
    'total_margin_percent_w2',
    'invoicing_markup',
    'misc_markup',
    'operational_markup',
  ],
  ['w2.travel']: [
    'base_charge_rate_cents',
    'charge_rate_cents',
    'base_margin_percent_w2_tcv',
    'base_margin_percent_w2_travel',
    'margin_percent_w2_travel',
    'margin_percent_w2_tcv',
    'margin_w2_tcv_cents',
    'invoicing_markup',
    'misc_markup',
    'operational_markup',
    'assignment_bonus_tier2_travel_amount_cents',
    'assignment_bonus_tier2_travel_amount_tcv_cents',
    'assignment_bonus_tier1_travel_amount_cents',
    'assignment_bonus_tier1_travel_amount_tcv_cents',
  ],
};

export const CALCULATED_FIELDS = [
  'margin_percent_1099',
  'charge_rate_cents',
  'total_margin_percent_w2',
  'margin_percent_w2_travel',
  'margin_percent_w2_tcv',
];

const CALCULATED_FIELD_BASE: Record<string, string> = {
  charge_rate_cents: 'base_charge_rate_cents',
  margin_percent_1099: 'base_margin_percent',
  total_margin_percent_w2: 'base_margin_percent_w2',
  margin_percent_w2_travel: 'base_margin_percent_w2_travel',
  margin_percent_w2_tcv: 'base_margin_percent_w2_tcv',
};

type FormValueReader = (path: string) => unknown;

const isBlankValue = (value: unknown): boolean =>
  value === null || value === undefined || value === '';

export const calculatedFieldPath = (
  source: string,
  field: string,
  getValue?: FormValueReader,
): string => {
  const baseField = CALCULATED_FIELD_BASE[field];
  if (!baseField) {
    return `${source}.${field}`;
  }
  const settingsSource = source.replace(/^[^.]+/, 'settings');
  return getValue && !isBlankValue(getValue(`${settingsSource}.${baseField}`))
    ? `${settingsSource}.${field}`
    : `${source}.${field}`;
};

const defaultNonPositionalHideFields: RateSettingsField[] = [
  'charge_rate_8hr',
  'charge_rate_12hr',
  'pro_rate',
  ...sources['ten99'],
  ...sources['w2.local'],
  ...sources['w2.travel'],
];

const markupFields: RateSettingsField[] = ['invoicing_markup', 'misc_markup', 'operational_markup'];
const fieldsWithoutMarkups: RateSettingsField[] = defaultNonPositionalHideFields.filter(
  (field) => !markupFields.includes(field),
);

export const RATE_SETTINGS_FIELD_VISIBILITY: FieldVisibilityConfig = {
  StateRateSetting: {
    hiddenFields: ['use_blended_rate', ...fieldsWithoutMarkups],
  },
  StatePositionRateSetting: {
    hiddenFields: [
      'use_blended_rate',
      'payout_max_weekly_regular_hours',
      'payout_seventh_day_rule_enabled',
      'charge_max_weekly_regular_hours',
      'charge_seventh_day_rule_enabled',
      'blended_non_blended_settings',
      'pro_rate',
    ],
  },
  StateLocationTypeRateSetting: {
    hiddenFields: ['use_blended_rate', ...defaultNonPositionalHideFields],
  },
  StateLocationTypePositionRateSetting: {
    hiddenFields: [
      'use_blended_rate',
      'payout_max_weekly_regular_hours',
      'payout_seventh_day_rule_enabled',
      'charge_max_weekly_regular_hours',
      'charge_seventh_day_rule_enabled',
      'blended_non_blended_settings',
      'pro_rate',
    ],
  },

  MarketRateSetting: {
    hiddenFields: ['use_blended_rate', ...defaultNonPositionalHideFields],
  },
  MarketPositionRateSetting: {
    hiddenFields: ['pro_rate'],
  },
  MarketLocationTypeRateSetting: {
    hiddenFields: ['use_blended_rate', ...defaultNonPositionalHideFields],
  },
  MarketLocationTypePositionRateSetting: {
    hiddenFields: ['use_blended_rate', 'pro_rate'],
  },

  LocationRateSetting: {
    hiddenFields: fieldsWithoutMarkups,
  },

  AgencyLocationRateSetting: {
    hiddenFields: defaultNonPositionalHideFields,
  },
  AgencyLocationPositionRateSetting: {
    hiddenFields: ['pro_rate'],
  },
};

export const isFieldVisible = (field: RateSettingsField, source: string): boolean => {
  const config = RATE_SETTINGS_FIELD_VISIBILITY[source];
  if (!config) {
    return true;
  }

  return !config.hiddenFields.includes(field);
};

type TaxClassification = 'w2.local' | 'w2.travel' | 'ten99';

type ShouldShowMarkupsArgs = {
  resource: string;
  taxClassification: TaxClassification;
  billingPlan: string | null | undefined;
  shouldShowInvoicingMarkup: boolean;
};

export const shouldShowMarkupsForTaxClassification = ({
  resource,
  taxClassification,
  billingPlan,
  shouldShowInvoicingMarkup,
}: ShouldShowMarkupsArgs): boolean => {
  const isLocationLevel = resource === 'LocationRateSetting';
  const isW2Location = billingPlan === 'enterprise_w2';

  if (isLocationLevel) {
    if (taxClassification === 'w2.travel') {
      return true;
    }

    if (taxClassification === 'w2.local') {
      return isW2Location;
    }

    return !isW2Location;
  }

  return shouldShowInvoicingMarkup;
};
